import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Switch,
  FormControlLabel,
  Button,
  Divider,
  IconButton,
  Collapse,
  Alert,
  Chip
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import featureFlags, { enableFeature, disableFeature, clearFeatureOverrides } from '../../config/featureFlags';

/**
 * Feature Flag Debug Panel
 * Only visible in development mode
 * Allows toggling feature flags for testing
 */
const FeatureFlagDebugPanel = () => {
  const [open, setOpen] = useState(false);
  const [flags, setFlags] = useState(featureFlags);
  const [needsReload, setNeedsReload] = useState(false);

  // Only show in development
  if (process.env.NODE_ENV !== 'development') {
    return null;
  }

  const handleToggle = (flagName) => {
    const currentValue = flags[flagName];
    if (currentValue) {
      disableFeature(flagName);
    } else {
      enableFeature(flagName);
    }
    setNeedsReload(true);
  };

  const handleClearAll = () => {
    clearFeatureOverrides();
    setNeedsReload(true);
  };

  const handleReload = () => {
    window.location.reload();
  };

  const flagDescriptions = {
    NEW_DASHBOARD: 'Enable new dashboard interface',
    NEW_NAV: 'Enable redesigned navigation',
    NEW_CHAT_LAYOUT: 'Enable enhanced chat interface',
    VOICE_MODE_V2: 'Enable improved voice mode',
    MOBILE_BOTTOM_NAV: 'Enable mobile bottom navigation',
    DEBUG_FLAGS: 'Show debug information in console'
  };

  return (
    <>
      {/* Floating button to open panel */}
      <IconButton
        onClick={() => setOpen(!open)}
        sx={{
          position: 'fixed',
          bottom: 16,
          right: 16,
          bgcolor: 'primary.main',
          color: 'white',
          zIndex: 9999,
          '&:hover': {
            bgcolor: 'primary.dark',
          },
          display: open ? 'none' : 'flex'
        }}
      >
        <SettingsIcon />
      </IconButton>

      {/* Debug panel */}
      <Collapse in={open}>
        <Paper
          elevation={8}
          sx={{
            position: 'fixed',
            bottom: 16,
            right: 16,
            width: 400,
            maxHeight: '80vh',
            overflow: 'auto',
            zIndex: 9999,
            p: 2
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ flexGrow: 1 }}>
              Feature Flags (Dev Only)
            </Typography>
            <IconButton size="small" onClick={() => setOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>

          <Divider sx={{ mb: 2 }} />

          {needsReload && (
            <Alert 
              severity="info" 
              sx={{ mb: 2 }}
              action={
                <Button 
                  size="small" 
                  startIcon={<RefreshIcon />}
                  onClick={handleReload}
                >
                  Reload
                </Button>
              }
            >
              Changes require page reload
            </Alert>
          )}

          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
            Toggle features for testing. Changes persist in localStorage.
          </Typography>

          {Object.entries(flags).map(([flagName, value]) => {
            if (flagName === 'USER_COHORT') return null; // Skip non-boolean flags
            
            return (
              <Box key={flagName} sx={{ mb: 2 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={Boolean(value)}
                      onChange={() => handleToggle(flagName)}
                      size="small"
                    />
                  }
                  label={
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography variant="body2" component="span">
                          {flagName.replace(/_/g, ' ')}
                        </Typography>
                        {value && (
                          <Chip 
                            label="Active" 
                            size="small" 
                            color="success" 
                            sx={{ ml: 1 }}
                          />
                        )}
                      </Box>
                      {flagDescriptions[flagName] && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          {flagDescriptions[flagName]}
                        </Typography>
                      )}
                    </Box>
                  }
                  sx={{ width: '100%', m: 0 }}
                />
              </Box>
            );
          })}

          <Divider sx={{ my: 2 }} />

          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              size="small"
              fullWidth
              onClick={handleClearAll}
            >
              Reset All
            </Button>
            <Button
              variant="contained"
              size="small"
              fullWidth
              onClick={handleReload}
              startIcon={<RefreshIcon />}
            >
              Reload Page
            </Button>
          </Box>

          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 2 }}>
            User Cohort: {flags.USER_COHORT || 'control'}
          </Typography>
        </Paper>
      </Collapse>
    </>
  );
};

export default FeatureFlagDebugPanel;